import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';

import { useProjectStore, useSuperHubStore } from '@core/store';

/**
 * WebMCP (Model Context Protocol for the Web) lets AI agents running in a
 * browser discover and call site-specific tools via
 * `navigator.modelContext.provideContext()`. We expose four tools on every
 * ReadMe-hosted docs page:
 *
 *   - `search_docs(query)`       — search the current project docs
 *   - `navigate_to_page(slug)`   — route the browser to a doc page
 *   - `get_current_page_content()` — return current page as markdown
 *   - `try_api_endpoint(...)`    — execute a try-it request via API Reference
 *
 * The hook is a no-op when the browser doesn't expose
 * `navigator.modelContext` (every browser today) or when the project has
 * opted out of WebMCP via `ai.discovery.webmcp`. Registered tools are
 * released on unmount.
 *
 * Spec: https://webmachinelearning.github.io/webmcp/
 */

interface ToolDefinition {
  description: string;
  execute: (args: Record<string, unknown>) => Promise<unknown>;
  inputSchema: Record<string, unknown>;
  name: string;
}

interface ModelContext {
  provideContext(context: { tools: ToolDefinition[] }): { release?: () => void } | undefined;
}

declare global {
  interface Navigator {
    modelContext?: ModelContext;
  }
}

export default function useWebMCP() {
  const history = useHistory();
  // Pull each value with its own selector so Zustand can compare them
  // by Object.is and skip re-renders on unrelated store updates.
  const aiDiscoveryFlag = useProjectStore(s => s.data.god_mode?.flags?.aiDiscovery);
  const aiDiscoveryEnabled = useProjectStore(s => s.data.ai?.discovery?.enabled);
  const aiDiscoveryWebmcp = useProjectStore(s => s.data.ai?.discovery?.webmcp);
  const projectName = useProjectStore(s => s.data.name);
  const isSuperHub = useSuperHubStore(s => s.isSuperHub);
  const getApiBaseUrl = useSuperHubStore(s => s.getApiBaseUrl);
  const currentBranchName = useSuperHubStore(s => s.currentBranch.name);

  useEffect(() => {
    // Server-side rendering: navigator is undefined.
    if (typeof navigator === 'undefined') return undefined;

    // Browser doesn't implement WebMCP yet — no-op. Keeps the feature
    // future-proof: once a user's AI agent exposes modelContext, the
    // tools light up automatically.
    if (!navigator.modelContext) return undefined;

    // Gated on the `aiDiscovery` rollout flag (RM-16627) — feature is
    // inert until it's enabled.
    if (!aiDiscoveryFlag) return undefined;

    // Respect per-project opt-out. Default-on semantics: undefined
    // values mean the field wasn't hydrated, so treat as enabled (the
    // schema default).
    if (aiDiscoveryEnabled === false || aiDiscoveryWebmcp === false) return undefined;

    const tools: ToolDefinition[] = [
      {
        name: 'search_docs',
        description: `Search the documentation site${projectName ? ` for ${projectName}` : ''}. Returns a list of relevant pages with titles and excerpts.`,
        inputSchema: {
          type: 'object',
          properties: {
            query: { type: 'string', description: 'Search query string.' },
          },
          required: ['query'],
        },
        async execute({ query }) {
          if (typeof query !== 'string' || !query.trim()) {
            return { error: 'query must be a non-empty string' };
          }
          // SuperHub projects expose search at api-next; legacy projects
          // search via client-side Algolia which isn't reachable from a
          // tool fetch. We support SuperHub here and surface a clear
          // error on legacy until an Algolia-backed path lands.
          if (!isSuperHub) {
            return { error: 'search is not available on legacy projects' };
          }
          const apiBaseUrl = getApiBaseUrl({ branch: null });
          const url = `${apiBaseUrl}/search?version=${encodeURIComponent(currentBranchName)}&query=${encodeURIComponent(query)}`;
          const res = await fetch(url, { headers: { Accept: 'application/json' } });
          if (!res.ok) return { error: `search failed (${res.status})` };
          return res.json();
        },
      },
      {
        name: 'navigate_to_page',
        description:
          'Navigate the browser to a specific documentation page by path. Use search_docs first to find candidate paths.',
        inputSchema: {
          type: 'object',
          properties: {
            path: {
              type: 'string',
              description: 'Relative path, e.g. "/docs/getting-started" or "/reference/create-widget".',
            },
          },
          required: ['path'],
        },
        async execute({ path }) {
          if (typeof path !== 'string' || !path.startsWith('/')) {
            return { error: 'path must be a relative path starting with /' };
          }
          history.push(path);
          return { ok: true, path };
        },
      },
      {
        name: 'get_current_page_content',
        description:
          'Return the current documentation page as markdown so the agent can reason about what the user is currently reading.',
        inputSchema: { type: 'object', properties: {} },
        async execute() {
          const res = await fetch(window.location.pathname, {
            headers: { Accept: 'text/markdown' },
          });
          if (!res.ok) return { error: `fetch failed (${res.status})` };
          const markdown = await res.text();
          return { url: window.location.pathname, markdown };
        },
      },
      {
        name: 'try_api_endpoint',
        description:
          'Execute a request against a specific API endpoint documented on this site. Requires the user to confirm in-browser before the request fires. Returns the response body and status. Accepts a HAR (HTTP Archive) request object so headers, query strings, and form-encoded bodies are first-class — same shape as the MCP `execute-request` tool.',
        inputSchema: {
          type: 'object',
          properties: {
            harRequest: {
              type: 'object',
              description: 'HAR request object.',
              properties: {
                method: {
                  type: 'string',
                  description: 'HTTP method, e.g. GET, POST, PUT, PATCH, DELETE, OPTIONS, HEAD.',
                },
                url: { type: 'string', description: 'Full absolute URL to call.' },
                headers: {
                  type: 'array',
                  description: 'Request headers as an array of {name, value} pairs.',
                  items: {
                    type: 'object',
                    properties: { name: { type: 'string' }, value: { type: 'string' } },
                    required: ['name', 'value'],
                  },
                },
                queryString: {
                  type: 'array',
                  description: 'Query parameters as an array of {name, value} pairs.',
                  items: {
                    type: 'object',
                    properties: { name: { type: 'string' }, value: { type: 'string' } },
                    required: ['name', 'value'],
                  },
                },
                postData: {
                  type: 'object',
                  description:
                    'Request body. Use `text` for raw bodies (JSON, plain text). Use `params` for form-encoded or multipart payloads.',
                  properties: {
                    mimeType: { type: 'string', description: 'e.g. application/json, multipart/form-data.' },
                    text: { type: 'string', description: 'Raw request body.' },
                    params: {
                      type: 'array',
                      items: {
                        type: 'object',
                        properties: { name: { type: 'string' }, value: { type: 'string' } },
                        required: ['name'],
                      },
                    },
                  },
                  required: ['mimeType'],
                },
              },
              required: ['method', 'url'],
            },
          },
          required: ['harRequest'],
        },
        async execute({ harRequest }) {
          const har = harRequest as
            | {
                method?: string;
                url?: string;
                headers?: { name: string; value: string }[];
                queryString?: { name: string; value: string }[];
                postData?:
                  | { mimeType: string; text?: string }
                  | { mimeType: string; params?: { name: string; value?: string }[] };
              }
            | undefined;
          if (!har || typeof har.method !== 'string' || typeof har.url !== 'string') {
            return { error: 'harRequest.method and harRequest.url are required' };
          }
          const method = har.method.toUpperCase();

          // v1: only safe, idempotent methods. Mutating methods need a
          // proper in-DOM confirmation UI that previews headers and body
          // (tracked separately, modeled on the MCP execute-request flow).
          // window.confirm() alone is not a sufficient gate for state-changing
          // requests since it shows only method + URL, not what's actually sent.
          const SAFE_METHODS = ['GET', 'HEAD', 'OPTIONS'];
          if (!SAFE_METHODS.includes(method)) {
            return {
              error: `try_api_endpoint currently supports ${SAFE_METHODS.join(', ')} only. Mutating methods will be supported once an in-browser request preview lands.`,
            };
          }

          let url: URL;
          try {
            url = new URL(har.url);
          } catch {
            return { error: 'harRequest.url must be a valid absolute URL' };
          }
          for (const { name, value } of har.queryString ?? []) {
            url.searchParams.append(name, value);
          }

          // Human-in-the-loop gate even for safe methods, to keep agents
          // from silently making cross-origin calls with the user's cookies.
          const approved = window.confirm(`Allow agent to ${method} ${url.toString()}?`);
          if (!approved) return { error: 'user declined' };

          const headers = new Headers();
          for (const { name, value } of har.headers ?? []) {
            headers.append(name, value);
          }

          // Body shaping mirrors fetch-har: `text` becomes the raw body
          // (JSON / plain), `params` becomes URLSearchParams or FormData
          // depending on the declared mimeType.
          let body: BodyInit | undefined;
          const allowBody = method !== 'GET' && method !== 'HEAD';
          if (allowBody && har.postData) {
            const mime = har.postData.mimeType;
            if (!headers.has('content-type')) headers.set('content-type', mime);
            if ('text' in har.postData && typeof har.postData.text === 'string') {
              body = har.postData.text;
            } else if ('params' in har.postData && Array.isArray(har.postData.params)) {
              if (mime.includes('multipart/form-data')) {
                const fd = new FormData();
                for (const { name, value } of har.postData.params) fd.append(name, value ?? '');
                body = fd;
                // Let the browser set the multipart boundary.
                headers.delete('content-type');
              } else {
                const params = new URLSearchParams();
                for (const { name, value } of har.postData.params) params.append(name, value ?? '');
                body = params;
              }
            }
          }

          try {
            const res = await fetch(url.toString(), { method, headers, body });
            const text = await res.text();
            return { status: res.status, body: text };
          } catch (err) {
            return { error: err instanceof Error ? err.message : String(err) };
          }
        },
      },
    ];

    const handle = navigator.modelContext.provideContext({ tools });

    return () => {
      handle?.release?.();
    };
  }, [
    aiDiscoveryFlag,
    aiDiscoveryEnabled,
    aiDiscoveryWebmcp,
    history,
    projectName,
    isSuperHub,
    getApiBaseUrl,
    currentBranchName,
  ]);
}
